/**
 * WhatToDoNextCard — Round 218A Phase H (2026-02-07).
 *
 * The "What should I do next?" panel on the Dashboard. Shows the
 * strategist's biggest opportunity, biggest risk, and recommended
 * first asset in one glance, with a button to open the full
 * Strategy Summary modal.
 *
 * Reads /api/onboarding/wow-summary. Cached by the backend so
 * repeat opens are free.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '@/utils/api';
import useNextAction from '@/hooks/useNextAction';
import { Target, Warning, Sparkle, ArrowRight, Package, RocketLaunch, Compass, TrendUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import StrategySummary from './StrategySummary';

const ASSET_META = {
  sales_page:   { label: 'Sales Page',   icon: RocketLaunch, route: '/sales-page-builder' },
  lead_magnet:  { label: 'Lead Magnet',  icon: Sparkle,      route: '/lead-magnet-builder' },
  content_pack: { label: 'Content Pack', icon: Compass,      route: '/dashboard' },
  offer_sheet:  { label: 'Offer Sheet',  icon: Package,      route: '/dashboard' },
  brand_kit:    { label: 'Brand Kit',    icon: Sparkle,      route: '/brand-brain-setup' },
  dfy_video:    { label: 'DFY Video',    icon: TrendUp,      route: '/dashboard' },
};


const WhatToDoNextCard = ({ onBuildFirstAsset = null }) => {
  const navigate = useNavigate();
  const nextAction = useNextAction(true);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await onboardingAPI.wowSummary();
        if (cancelled) return;
        setSummary(res?.summary || null);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="what-to-do-next-loading"
        className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5"
      >
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-1/3 bg-slate-700/40 rounded" />
          <div className="h-3 w-full bg-slate-700/30 rounded" />
          <div className="h-3 w-5/6 bg-slate-700/30 rounded" />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    // Fail gracefully — don't block the dashboard.
    return null;
  }

  const opp = summary.biggest_opportunity || {};
  const risk = summary.biggest_risk || {};
  const first = summary.recommended_first_asset || {};
  const meta = ASSET_META[first.asset_kind] || {};
  const AssetIcon = meta.icon || Package;
  const assetLabel = meta.label || 'First Asset';

  const handleBuildFirst = () => {
    // Round 218A P0 FINAL — canonical resolver decides the route.
    // Never lands on a downstream builder before Brand Brain.
    if (nextAction.nextRoute) {
      navigate(nextAction.nextRoute);
      return;
    }
    if (typeof onBuildFirstAsset === 'function') {
      onBuildFirstAsset(first);
    } else if (meta.route) {
      navigate(meta.route);
    }
  };

  return (
    <>
      <div
        data-testid="what-to-do-next-card"
        className="mb-6 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.08] via-slate-900/40 to-slate-900/40 p-4 sm:p-5"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 text-emerald-300 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkle size={14} weight="fill" /> What should I do next?
          </div>
          <button
            onClick={() => setOpen(true)}
            className="text-xs text-slate-300 hover:text-white underline underline-offset-4 decoration-slate-600 hover:decoration-white"
            data-testid="what-to-do-next-open-summary"
          >
            View full strategy
          </button>
        </div>

        {summary.hero_headline && (
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1 leading-tight" data-testid="what-to-do-next-headline">
            {summary.hero_headline}
          </h2>
        )}

        {/* 3-column strategic snapshot */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          {opp.title && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-3" data-testid="what-to-do-next-opportunity">
              <div className="flex items-center gap-1.5 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider mb-1">
                <Target size={12} weight="fill" /> Opportunity
              </div>
              <div className="text-xs text-white font-semibold leading-snug">{opp.title}</div>
              {opp.expected_impact && (
                <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{opp.expected_impact}</div>
              )}
            </div>
          )}
          {risk.title && (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3" data-testid="what-to-do-next-risk">
              <div className="flex items-center gap-1.5 text-amber-300 text-[10px] font-semibold uppercase tracking-wider mb-1">
                <Warning size={12} weight="fill" /> Risk
              </div>
              <div className="text-xs text-white font-semibold leading-snug">{risk.title}</div>
              {risk.mitigation && (
                <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{risk.mitigation}</div>
              )}
            </div>
          )}
          {first.title && (
            <div className="rounded-xl border border-blue-500/25 bg-blue-500/[0.06] p-3" data-testid="what-to-do-next-first-asset">
              <div className="flex items-center gap-1.5 text-blue-300 text-[10px] font-semibold uppercase tracking-wider mb-1">
                <AssetIcon size={12} weight="fill" /> First asset · {assetLabel}
              </div>
              <div className="text-xs text-white font-semibold leading-snug">{first.title}</div>
              {first.why && (
                <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{first.why}</div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-4">
          <button
            onClick={handleBuildFirst}
            data-testid="what-to-do-next-build-first-asset-cta"
            className="flex-1 sm:flex-none px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors text-sm"
          >
            {nextAction.nextCtaLabel || 'Continue'} <ArrowRight size={16} weight="bold" />
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="what-to-do-next-see-more"
            className="flex-1 sm:flex-none px-5 py-2.5 bg-slate-800/70 hover:bg-slate-800 text-white font-medium rounded-xl transition-colors text-sm"
          >
            See strategist&rsquo;s full brief
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800"
          data-testid="strategy-summary-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-emerald-300" />
              Your strategist&rsquo;s brief
            </DialogTitle>
          </DialogHeader>
          <StrategySummary
            summary={summary}
            autoFetch={false}
            onBuildFirstAsset={(asset) => {
              setOpen(false);
              if (typeof onBuildFirstAsset === 'function') {
                onBuildFirstAsset(asset);
              } else if ((ASSET_META[asset?.asset_kind] || {}).route) {
                navigate(ASSET_META[asset.asset_kind].route);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhatToDoNextCard;
